/**
 * Constructs the pair of HTTP clients used by the auth package.
 *
 * - `httpClient`        — targets the client-app (Rails) API with full
 *                         Zoocasa headers.
 * - `cognitoHttpClient` — targets the auth API with Cognito-specific
 *                         headers (no request-source / generation markers).
 *
 * @module
 */
import { createAuthHttpClient } from './client';
import { getDefaultHeaders, getCognitoDefaultHeaders } from './headers';
import { createGetBaseUrl } from './base-url';
import type { HttpClient } from './client';

/** Host configuration accepted by {@link buildHttpClients}. */
export interface BuildHttpClientsOptions {
  /** Primary client-app host (e.g. `https://www.zoocasa.com`). */
  clientAppHost?: string;
  /** Optional auth-API host override.  Defaults to `clientAppHost`. */
  authApiHost?: string;
}

/**
 * Builds and returns the two HTTP clients required by auth services.
 *
 * Each client is configured with the appropriate base-URL resolver and
 * header provider so callers never need to think about host switching
 * or header differences between the Rails API and the Cognito auth API.
 */
export function buildHttpClients(options: BuildHttpClientsOptions): { httpClient: HttpClient; cognitoHttpClient: HttpClient } {
  const getBaseUrl = createGetBaseUrl(options.clientAppHost);
  const getAuthApiBaseUrl = createGetBaseUrl(options.clientAppHost, options.authApiHost);
  return {
    httpClient: createAuthHttpClient({ getBaseUrl, getDefaultHeaders }),
    cognitoHttpClient: createAuthHttpClient({ getBaseUrl: getAuthApiBaseUrl, getDefaultHeaders: getCognitoDefaultHeaders }),
  };
}
